import { ColumnHeightOutlined, ColumnWidthOutlined, DeleteOutlined, HistoryOutlined, MinusOutlined, PlusOutlined, RedoOutlined, ThunderboltOutlined, UndoOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Components } from '@fasstech/flexiget';
import { Button, Input } from 'antd';
import { always, and, assocPath, cond, equals, gt, includes, indexOf, isNotEmpty, isNotNil, lt, not, or, path, prop, propOr, sortBy, T, toLower, any, propEq, append, map } from 'ramda';
import { useLocation, useHistory } from 'react-router-dom';
import { isNotNilOrEmpty, isTrue } from 'ramda-adjunct';
import classNames from 'classnames';
import { Section, Title } from '../../../../components';
import { editorWidth, updateLayout, withEditing } from '../helpers/widget';
import DropZone from '../layoutItems/DropZone';
import { getFlexigetOptionsElement, getFlexigetSizeElements } from '../helpers/flexigets';
import useColors from '../../../../hooks/useColors';
import useStyles from '../../../../hooks/useStyles';
import useDefaultFont from '../../../../hooks/useDefaultFont';
import ElementsBar from '../layoutItems/ElementsBar';
import ConfirmButton from '../../../../components/ConfirmButton';
import Loading from '../../../../components/Loading';
import { Rule } from '../rules/DocumentRules';
import Card from '../layoutItems/Card';
import CssVariables from '../document/CssVariables';
import Switch from '../../../../components/Switch';
import { ACTION_REDO, ACTION_UNDO, SCOPE_OFFER } from '../../../../_CONST';
import useHistoryStack from '../../../../hooks/useHistoryStack';
import GlobalVariableForm from '../globalVariable/GlobalVariableForm';
import StyleFormPreview from '../../../elements/styles/components/StyleFormPreview';
import SelectOrientation from './SelectOrientation';
import WidgetIsUsed from './WidgetIsUsed';
import SearchBar from './SearchBar';
import GridInputNumber from './GridInputNumber';
import VariablesLibrary from './variablesLibrary/VariablesLibrary';

const useToolbarScroll = ({ modalMode, isFocus }) => {
  const [showYScrollBar, setShowYScrollBar] = useState(false);
  const [yScrollBarPosition, setYScrollBarPosition] = useState(0);
  const [isToolbarFixed, setIsToolbarFixed] = useState(false);
  const [toolbarFixedStyle, setToolbarFixedStyle] = useState({});

  const sectionRef = useRef(null);
  const toolbarRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    if (yScrollBarPosition >= 880) {
      setShowYScrollBar(true);
    } else {
      setShowYScrollBar(false);
    }
  }, [yScrollBarPosition]);

  useEffect(() => {
    let scrollElement = window;
    if (modalMode) {
      const modalBody = document.querySelector('.ant-modal-body');
      if (modalBody) scrollElement = modalBody;
    }

    const handleScroll = (() => {
      return () => {
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

        scrollTimeoutRef.current = setTimeout(() => {
          if (!sectionRef.current || !toolbarRef.current) return;
          const isAnyElementFocused = isFocus.some(focus => focus);
          if (!isAnyElementFocused) {
            setIsToolbarFixed(false);
            setToolbarFixedStyle({});
            return;
          }
          const sectionRect = sectionRef.current.getBoundingClientRect();
          const sectionStyle = window.getComputedStyle(sectionRef.current);
          const marginLeft = parseFloat(sectionStyle.marginLeft) || 0;
          const marginRight = parseFloat(sectionStyle.marginRight) || 0;
          const totalWidth = sectionRect.width + marginLeft + marginRight;
          const maxBottom = modalMode ? 90 : 160;
          if (sectionRect.bottom < maxBottom) {
            setIsToolbarFixed(true);
            setToolbarFixedStyle({
              position: 'fixed',
              top: modalMode ? '5vh' : '96px',
              left: sectionRect.left - marginLeft,
              width: totalWidth,
              zIndex: 1000,
              background: '#fff',
              borderBottom: '1px solid #eee',
              boxShadow: '0 2px 8px var(--flexi-placeholder)',
              minHeight: '40px',
              paddingLeft: sectionStyle.paddingLeft,
              paddingRight: sectionStyle.paddingRight,
              paddingTop: '8px',
              paddingBottom: sectionStyle.paddingBottom
            });
          } else {
            setIsToolbarFixed(false);
            setToolbarFixedStyle({});
          }
        }, 10); // Delay of 10ms to debounce scroll events
      };
    })();

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [modalMode, isFocus]);

  return {
    showYScrollBar, setYScrollBarPosition, sectionRef, toolbarRef, isToolbarFixed, toolbarFixedStyle
  };
};

const WidgetFormCore = ({ isLateEdit = false, modalMode = false, offerId, values, preview = false, isEdit, widgetExist, userCanEdit, setState, scale = 1, totalPaddingX, elements, flexigets, isUsedBy = [], isUsed = false, isEditing = false, onDelete, deleteLoading, onCancel, onSave, loading, hasVariableOnRulesNotInVariables = false, isValid, isOpenCreateGlobalVariableModal, onSaveAndQuit, updateRule, removeRule, getConditionsAreValid, setIsOpenCreateGlobalVariableModal, variablesForRules, setGlobalVariableCreated, documentVariables, predefinedVariables = [] }) => {
  const { pathname } = useLocation();
  const history = useHistory();

  const [isDownNbColsGrid, setIsDownNbColsGrid] = useState();
  const [isFrameConditionedElements, setIsFrameConditionedElements] = useState(!isLateEdit);
  const [isFocus, setIsFocus] = useState([]);
  const [useCSSTransforms, setUseCSSTransforms] = useState(true);
  const [isClosedElement, setIsClosedElement] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleWidgets, setVisibleWidgets] = useState([]);
  const [showCreateStyleModal, setShowCreateStyleModal] = useState(false);
  const [layout, setLayout] = useState([]);

  const { colors } = useColors();
  const { styles, reFetch: reFetchStyle } = useStyles();
  const { defaultFont, isLoading } = useDefaultFont();

  const { showYScrollBar, setYScrollBarPosition, sectionRef, toolbarRef, isToolbarFixed, toolbarFixedStyle } = useToolbarScroll({ modalMode, isFocus });

  const contents = prop('contents', values);

  const hasDynamicTable = useMemo(() => {
    return any(propEq('dynamicTable', 'type'), contents);
  }, [contents]);

  const orderElementsByAsc = (elements) => sortBy(prop('label'))(elements);

  useEffect(() => {
    if (searchTerm) {
      const filteredList = widgetTypes.filter((widget) => {
        return prop('label', widget).toLowerCase().includes(searchTerm.toLowerCase());
      });
      setVisibleWidgets(orderElementsByAsc(filteredList));
    } else {
      setVisibleWidgets(orderElementsByAsc(widgetTypes));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const widgetIsEditable = and(or(isEdit, widgetExist), not(preview));

  const title = cond([
    [() => widgetIsEditable, always('Modifier le widget')],
    [() => isTrue(preview), always('Prévisualiser le widget')],
    [T, always('Créer un widget')]
  ])();

  const backToTitle = cond([
    [() => modalMode, always(null)],
    [includes('/offre'), always(`/offre/${offerId}/edition`)],
    [includes('/equipe'), always('/equipe/widgets')]
  ])(pathname);

  const widgetName = prop('name', values);
  const addPrefixToWidgetNameIfLandscape = cond([
    [equals('landscape'), always(`[PAYSAGE] - ${widgetName}`)],
    [T, always(widgetName)]
  ])(prop('orientation', values));
  const handleWidgetName = isNotNilOrEmpty(widgetName) ? widgetName : addPrefixToWidgetNameIfLandscape;
  const inputsAreDisabled = preview || !userCanEdit || isLateEdit;

  const changeNbColsGrid = (value) => {
    setIsDownNbColsGrid(lt(value, prop('nbColsGrid', values)));
    setState(prevState => withEditing(
      assocPath(['values', 'nbColsGrid'], value, prevState)
    ));
  };
  const changeHeightRowsGrid = (value) => setState(prevState => withEditing(
    assocPath(['values', 'heightRowsGrid'], value, prevState)
  ));
  const changeOrientation = (value) => setState(prevState => withEditing(
    assocPath(['values', 'orientation'], value, prevState)
  ));

  const indexFocus = indexOf(true, isFocus);
  const yScrollBarStyle = showYScrollBar ? 'overflow-y-scroll' : 'overflow-y-hidden';
  const yScrollElements = visibleWidgets.length > 10 ? 'h-750px overflow-y-auto' : '';

  const hasPaddingX = gt(totalPaddingX, 0);

  const widgetMinSize = getFlexigetSizeElements(flexigets);
  const widgetTypes = getFlexigetOptionsElement(flexigets);
  const canEdit = !preview && userCanEdit;
  // Reprise mode strips every side panel, and VariablesLibrary renders nothing without a
  // source. This single flag drives everything the left column affects: the canvas
  // width, the read-only spacer and the fixed action bar offsets, so they never drift
  // apart. Without the library (reprise) the historical `w-10/12` layout is kept as is.
  const hasVariablesLibrary = !isLateEdit && (isNotNil(documentVariables) || isNotEmpty(predefinedVariables));
  const canvasWidthClass = hasVariablesLibrary ? 'flex-1 min-w-0' : 'w-10/12';

  const widgetConditionMessageOnSave = (
    <>
      Votre widget contient des conditions basées sur des variables qui n’existent plus.
      <br />
      Souhaitez-vous supprimer ces conditions et enregistrer ?
    </>
  );
  const isInCreation = and(not(widgetExist), not(preview));
  const hasRightToEditWidget = userCanEdit && (widgetIsEditable || isEdit || isInCreation);

  const closeCreateStyleModal = (ok) => {
    if (equals(true, ok)) {
      reFetchStyle();
    }
    setShowCreateStyleModal(false);
  };

  const isAddElementAction = (type) => any(equals(type), ['addNewElement', 'onDuplicateElement']);
  const isRemoveElementAction = (type) => equals(type, 'onDeleteElement');
  const shouldFocus = useCallback((type, isUndo) => (isAddElementAction(type) && !isUndo) || (isRemoveElementAction(type) && isUndo), []);

  const {
    isUndoPreviousEditorState,
    setIsUndoPreviousEditorState,
    historyActions,
    addActionOnPrevious,
    addToHistory,
    applyHistoryAction,
    canUndo,
    canRedo
  } = useHistoryStack({
    onApply: useCallback((value) => setState((prevState) => withEditing(assocPath(['values', 'contents'], value, prevState))), [setState]),
    onLayoutSync: useCallback((value, type, isUndo) => {
      const { nbColsGrid, heightRowsGrid, orientation } = values;
      updateLayout(
        value,
        nbColsGrid,
        heightRowsGrid,
        setLayout,
        hasRightToEditWidget,
        isDownNbColsGrid,
        elements,
        widgetMinSize,
        orientation
      );
     
      if (shouldFocus(type, isUndo)) {
        setIsFocus(append(true, map(always(false), contents)));
        setUseCSSTransforms(isRemoveElementAction(type));
      }
    }, [values, setLayout, hasRightToEditWidget, isDownNbColsGrid, elements, widgetMinSize, contents, shouldFocus])
  });

  const handleUndo = useCallback(() => applyHistoryAction(ACTION_UNDO), [applyHistoryAction]);
  const handleRedo = useCallback(() => applyHistoryAction(ACTION_REDO), [applyHistoryAction]);

  useEffect(() => {
    const platform = path(['userAgentData', 'platform'], window.navigator) || prop('platform', window.navigator);
    const userAgent = prop('userAgent', window.navigator);
    const platformArray = [toLower(platform), toLower(userAgent)];
    if (any(includes('mac'), platformArray)) {
      const handleKeyDown = (event) => {
        if (event.metaKey && toLower(event.key) === 'y') {
          event.preventDefault();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const { ctrlKey, metaKey, key, shiftKey } = event;
      const isCtrlOrMetaKey = (ctrlKey || metaKey);
      const isUndoKey = equals(key, 'z');
      const isRedoKey = equals(toLower(key), 'y');
      const isShiftAndZ = shiftKey && equals(toLower(key), 'z');

      if (isCtrlOrMetaKey && isUndoKey) {
        event.preventDefault();
        event.stopPropagation();
        handleUndo();
      } else if (isCtrlOrMetaKey && (isRedoKey || isShiftAndZ)) {
        event.preventDefault();
        event.stopPropagation();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUndo, handleRedo]);

  const handleUpdateRule = (fn) => {
    const newState = updateRule(indexFocus)(fn);
    addToHistory(contents, path(['values', 'contents'], newState), 'updateRule');
  };

  const handleRemoveRule = () => {
    const newState = removeRule(indexFocus)();
    addToHistory(contents, path(['values', 'contents'], newState), 'removeRule');
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <CssVariables />
      <div
        className="relative z-40"
        // eslint-disable-next-line no-restricted-syntax
        style={isLateEdit ? {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${100 / scale}%`
        } : {}}
      >
        <div className="relative flex w-full items-center min-h-16">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-80">
            <Title
              title={title}
              backTo={backToTitle}
            />
            {(isEdit || widgetExist) && !modalMode && (
              <Button
                size="small"
                onClick={() => {
                  const widgetId = prop('id', values);
                  const to = cond([
                    [includes('/offre'), always(`/offre/${offerId}/widgets/${widgetId}/historique`)],
                    [includes('/equipe'), always(`/equipe/widgets/${widgetId}/historique`)]
                  ])(pathname);
                  history.push(to);
                }}
                icon={<HistoryOutlined/>}
              >
                Historique du widget
              </Button>
            )}
          </div>
          {canEdit && isUsed && (
            <WidgetIsUsed isUsedBy={isUsedBy} isEditing={isEditing} modalMode={modalMode} />
          )}
        </div>
        <Section
          title="Nom du widget"
          className="flex flex-col space-y-2"
          ref={sectionRef}
        >
          <div className="flex gap-x-6">
            <Input
              placeholder="Nom du widget"
              value={handleWidgetName}
              onChange={e => {
                const value = path(['target', 'value'], e);
                setState(prevState => withEditing(
                  assocPath(['values', 'name'], value, prevState)
                ));
              }}
              className={isLateEdit ? 'w-full' : 'w-1/3'}
              disabled={inputsAreDisabled}
            />
            <div className="flex space-x-2">
              <Button
                className="btn-undo-redo !rounded-sm border cursor-pointer"
                disabled={!canUndo}
                icon={<UndoOutlined />}
                onClick={handleUndo}
              />
              <Button
                className="btn-undo-redo !rounded-sm border cursor-pointer"
                disabled={!canRedo}
                icon={<RedoOutlined />}
                onClick={handleRedo}
              />
            </div>
            {!isLateEdit && (
              <>
                <Components.Tooltip title="Nombre de colonnes">
                  <GridInputNumber
                    addonBefore={<ColumnWidthOutlined/>}
                    min={1}
                    controls={{
                      upIcon: <PlusOutlined/>,
                      downIcon: <MinusOutlined/>
                    }}
                    value={prop('nbColsGrid', values)}
                    onChange={changeNbColsGrid}
                    className="w-32"
                    disabled={inputsAreDisabled}
                  />
                </Components.Tooltip>
                <Components.Tooltip title="Hauteur minimale d'une ligne">
                  <GridInputNumber
                    addonBefore={<ColumnHeightOutlined/>}
                    max={50}
                    min={1}
                    controls={{
                      upIcon: <PlusOutlined/>,
                      downIcon: <MinusOutlined/>
                    }}
                    value={prop('heightRowsGrid', values)}
                    onChange={changeHeightRowsGrid}
                    className="w-32"
                    disabled={inputsAreDisabled}
                  />
                </Components.Tooltip>
                <SelectOrientation
                  defaultOrientation={prop('orientation', values)}
                  changeOrientation={changeOrientation}
                  disabled={inputsAreDisabled}
                />
                <Switch
                  className="flex justify-center items-center"
                  checked={isFrameConditionedElements}
                  onChange={setIsFrameConditionedElements}
                  onClick={() => setIsFrameConditionedElements(!isFrameConditionedElements)}
                  label="Encadrer les éléments conditionnés"
                />
              </>
            )}
          </div>

          <div className="min-h-10">
            <div
              ref={toolbarRef}
              // eslint-disable-next-line no-restricted-syntax
              style={isToolbarFixed ? toolbarFixedStyle : {}}
              className={classNames('z-50', { 'relative w-full': !isToolbarFixed })}
            >
              <div id="element-toolbar" className="flexiget" />
            </div>
          </div>
        </Section>
      </div>
      <div
        // eslint-disable-next-line no-restricted-syntax
        style={{ zoom: scale }}
      >
        {/* avec transform scale impossible de focus sur les éléments correctement */}
        <div className="flex items-start justify-between gap-x-4 pb-16">
          {hasVariablesLibrary && (
            <VariablesLibrary documentVariables={documentVariables} predefinedVariables={predefinedVariables} />
          )}
          {/* `max-h` rather than the historical `h-910px` (2022, when the canvas was a
              fixed 800x910 viewport): the height was reserved whatever the widget
              contained, leaving hundreds of dead pixels below short widgets. The drop
              target is `.section-widget-zone` and has always been content-sized, so
              shrinking this box costs no drop surface. Above 910px the box caps and
              scrolls, exactly as before. */}
          <div className={`max-h-910px ${canvasWidthClass} pb-6 scrollbar mx-auto overflow-x-auto pt-6 pr-6 ${yScrollBarStyle}`}>
            {/* react-grid-layout renders its full-width dropping placeholder at the raw cursor
            position while a CARD hovers the grid, which can overflow to the right and pop a
            horizontal scrollbar. `clip` only cuts this axis; overflow-y stays untouched by
            design */}
            <div
              className={`section-widget-zone mx-auto bg-white relative overflow-x-clip ${hasPaddingX ? 'mt-32' : 'mt-0'} ${hasPaddingX && showYScrollBar ? 'mb-32' : 'mb-0'}`}
              // eslint-disable-next-line no-restricted-syntax
              style={{ width: `${editorWidth(propOr('portrait', 'orientation', values))}px` }}
            >
              {hasPaddingX && (
              // eslint-disable-next-line no-restricted-syntax
                <><div className="absolute h-36 -top-36 border-r border-flexi-primary-color border-dotted" style={{ right: `${totalPaddingX}px` }} />
                  {/* eslint-disable-next-line no-restricted-syntax */}
                  <div className="absolute -top-36 bg-flexi-primary-color text-white text-xs p-1.5" style={{ right: `${totalPaddingX}px` }}>
                    <p>{'Le document s\'arrête ici'}</p>
                  </div>
                  {/* eslint-disable-next-line no-restricted-syntax */}
                  <div className="absolute h-36 -bottom-36 border-r border-flexi-primary-color border-dotted" style={{ right: `${totalPaddingX}px` }} />
                </>
              )}
              <DropZone
                values={values}
                setState={setState}
                isDownNbColsGrid={isDownNbColsGrid}
                setYScrollBarPosition={setYScrollBarPosition}
                widgetIsEditable={hasRightToEditWidget}
                elements={elements}
                optionsElement={widgetTypes}
                widgetMinSize={widgetMinSize}
                defaultFont={defaultFont}
                customColors={colors}
                customStyles={styles}
                isFocus={isFocus}
                setIsFocus={setIsFocus}
                useCSSTransforms={useCSSTransforms}
                setUseCSSTransforms={setUseCSSTransforms}
                onCreateStyle={() => setShowCreateStyleModal(true)}
                isOpenCreateGlobalVariableModal={isOpenCreateGlobalVariableModal}
                isFrameConditionedElements={isFrameConditionedElements}
                toolbarRef={toolbarRef}
                isToolbarFixed={isToolbarFixed}
                isLateEdit={isLateEdit}
                scale={scale}
                addToHistory={addToHistory}
                layout={layout}
                setLayout={setLayout}
                isUndoPreviousEditorState={isUndoPreviousEditorState}
                setIsUndoPreviousEditorState={setIsUndoPreviousEditorState}
                historyActions={historyActions}
                addActionOnPrevious={addActionOnPrevious}
              />
            </div>
          </div>

          {canEdit && (
            <div className="w-210px space-y-2 z-10" id="style-bar">
              {!isLateEdit && (
                <ElementsBar
                  title="ajouter élément"
                  isClosed={isClosedElement}
                  setIsClosed={setIsClosedElement}
                >
                  <div
                    className={classNames(
                      'w-full',
                      { hidden: isClosedElement }
                    )}
                  >
                    <SearchBar
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                    <div className={`grid grid-cols-1 gap-2 scrollbar ${yScrollElements}`}>
                      {visibleWidgets.map(({
                        key,
                        label
                      }) => (
                        <Card
                          key={key}
                          type={key}
                          label={label}
                          hasDynamicTable={hasDynamicTable}
                          setIsFocus={setIsFocus}
                          setUseCSSTransforms={setUseCSSTransforms}
                        />
                      ))}
                    </div>
                  </div>
                </ElementsBar>
              )}
              {includes(path([indexFocus, 'type'], contents), ['dataMatrix', 'dynamicTable']) && (
                <ElementsBar title="mise en page - élément">
                  <div id="element-toolbar-old" className="flexiget" />
                </ElementsBar>
              )}
              {!isLateEdit && (
                <div>
                  {indexFocus >= 0 && (
                    <ElementsBar title="ajouter une règle">
                      <Rule
                        rule={path([indexFocus, 'rule'], contents)}
                        variables={variablesForRules}
                        onUpdate={handleUpdateRule}
                        onRemove={handleRemoveRule}
                        ruleIndex={indexFocus}
                        getConditionsAreValid={getConditionsAreValid}
                        preview={not(hasRightToEditWidget)}
                        userCanEdit={userCanEdit}
                        setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
                        isWidget
                      />
                    </ElementsBar>
                  )}
                </div>
              )}
              {!isLateEdit && (
                <div>
                  <ElementsBar title="Variables">
                    <div id="element-variable-bar"/>
                  </ElementsBar>
                </div>
              )}
            </div>
          )}
          {/* Read-only views render no #style-bar: this spacer stands in for it so the
              canvas stays centred between two symmetric 210px columns. */}
          {hasVariablesLibrary && !canEdit && <div className="w-210px shrink-0" />}
        </div>
        {canEdit && (
          <div
            className={classNames(
              'fixed flex flex-col items-center justify-center mt-4 w-fit m-auto z-10',
              { 'bottom-0 right-210px p-3': !modalMode },
              { 'left-[240px]': !modalMode && !hasVariablesLibrary },
              // With the library the canvas is centred on the viewport (symmetric page
              // padding + two 210px columns), so the insets must be symmetric too:
              // the bar centres itself between them through `w-fit m-auto`.
              { 'left-210px': !modalMode && hasVariablesLibrary },
              { 'p-11 right-400px bottom-12': modalMode },
              { 'left-210px': modalMode && !hasVariablesLibrary },
              { 'left-400px': modalMode && hasVariablesLibrary }
            )}
          >
            <div className="btns-actions flex gap-x-4">
              {widgetIsEditable && !modalMode && (
                <ConfirmButton
                  title="Êtes-vous sûr de vouloir supprimer ce widget ?"
                  onConfirm={onDelete}
                  buttonType="danger"
                  loading={deleteLoading}
                  icon={<DeleteOutlined className="btn-remove-widget" />}
                  buttonText="Supprimer le widget"
                  disabled={isUsed}
                />
              )}
              {widgetIsEditable && modalMode && (
                <Button
                  className="text-sm"
                  onClick={onCancel}
                >
                  Annuler
                </Button>
              )}
              {!modalMode && (
                <ConfirmButton
                  title={widgetConditionMessageOnSave}
                  onConfirm={onSave(true)}
                  buttonType="primary"
                  className="widget-save-button"
                  loading={loading}
                  icon={<ThunderboltOutlined />}
                  buttonText="Enregistrer"
                  disabledPopover={!hasVariableOnRulesNotInVariables || isLateEdit}
                  disabled={!isValid}
                />
              )}

              <ConfirmButton
                title={widgetConditionMessageOnSave}
                onConfirm={onSaveAndQuit}
                buttonType="primary"
                className="widget-save-and-quit-button"
                loading={loading}
                icon={<ThunderboltOutlined />}
                buttonText={modalMode ? 'Enregistrer' : 'Enregistrer et quitter'}
                disabledPopover={!hasVariableOnRulesNotInVariables}
                disabled={!isValid}
              />
            </div>
          </div>
        )}
      </div>
      <StyleFormPreview
        modalMode
        displayBackBtn={false}
        open={showCreateStyleModal}
        closeModal={closeCreateStyleModal}
      />
      <GlobalVariableForm
        isOpenCreateGlobalVariableModal={isOpenCreateGlobalVariableModal}
        setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
        scope={SCOPE_OFFER}
        offerId={offerId}
        modalMode
        setGlobalVariableCreated={setGlobalVariableCreated && setGlobalVariableCreated(indexFocus)}
      />
    </div>
  );
};
export default WidgetFormCore;
